import React, { useMemo } from "react";
import { useRefinementList } from "react-instantsearch";
import { map } from "lodash";
import FilterSection from "../../../components/pages/SearchPage/components/FilterSection";
import styled from "styled-components";
import CheckIcon from "@material-ui/icons/Check";

type SwatchColour = {
  id: string;
  bg: string;
  invertColour: boolean;
};
export const colours: SwatchColour[] = [
  { id: "White", bg: "#ffffff", invertColour: true },
  { id: "Black", bg: "#000000", invertColour: false },
  { id: "Silver", bg: "#d2d6dc", invertColour: true },
  { id: "Brown", bg: "#b43403", invertColour: false },
  { id: "Red", bg: "#f05252", invertColour: false },
  { id: "Pink", bg: "#f17eb8", invertColour: false },
  { id: "Orange", bg: "#ff8a4c", invertColour: false },
  { id: "Yellow", bg: "#faca15", invertColour: false },
  { id: "Green", bg: "#31c48d", invertColour: false },
  { id: "Beige", bg: "#fcd9bd", invertColour: true },
  { id: "Grey", bg: "#808080", invertColour: false },
  { id: "Blue", bg: "#76a9fa", invertColour: false },
  { id: "Purple", bg: "#ac94fa", invertColour: false },
  { id: "Gold", bg: "#FFD700", invertColour: false },
  { id: "Khaki", bg: "#F0E68C", invertColour: true },
  { id: "Metallic", bg: "#aaa9ad", invertColour: false },
  { id: "Navy", bg: "#000080", invertColour: false },
  { id: "Cream", bg: "#F6F0BC", invertColour: true },
];

export default function ColourFilter() {
  const { items, refine, canRefine } = useRefinementList({ attribute: "colour", limit: 100 });
  const selectedValues = items.filter((item) => item.isRefined).map((item) => item.value);
  const hasSelectedItems = selectedValues.length > 0;

  const optionLabels = useMemo<string[]>(() => items.map(({ label }) => label), [items]);
  const availableColours = useMemo<SwatchColour[]>(
    () => colours.filter(({ id }) => optionLabels.includes(id)),
    [optionLabels]
  );

  return (
    <FilterSection label="Colour" canRefine={canRefine} hasSelectedItems={hasSelectedItems}>
      <div className="t-searchFilterGroup">
        {map(availableColours, (colour) => (
          <ColourToggle
            key={colour.id}
            onClick={() => refine(colour.id)}
            selected={selectedValues.includes(colour.id)}
            swatchColour={colour}
          />
        ))}
      </div>
    </FilterSection>
  );
}

type ColourToggleProps = {
  selected: boolean;
  swatchColour: SwatchColour;
  onClick: () => void;
};

function ColourToggle({ selected, swatchColour, ...props }: ColourToggleProps) {
  return (
    <ToggleContainer $swatchColour={swatchColour} {...props}>
      <CheckmarkIcon $swatchColour={swatchColour} selected={selected} />
    </ToggleContainer>
  );
}

type SwatchColorProps = {
  $swatchColour: SwatchColour;
};

const ToggleContainer = styled.div<SwatchColorProps>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  margin-top: 0.5em;
  margin-left: 0.5em;
  border-width: 1px;
  border-style: solid;
  cursor: pointer;
  width: 1.75em;
  height: 1.75em;
  border-radius: 0.375em;
  background-color: ${({ $swatchColour }) => $swatchColour.bg};
  border-color: ${(props) => getBorderColor(props)};
`;

const CheckmarkIcon = styled(CheckIcon)<SwatchColorProps & { selected: boolean }>`
  width: 100%;
  height: 100%;
  fill: ${(props) => getCheckmarkColor(props)};
  overflow: hidden;
  ${(props) =>
    !props.selected &&
    `
    visibility: hidden;
  `}
`;

function getBorderColor({ $swatchColour }: SwatchColorProps) {
  return $swatchColour.invertColour ? "#ccc" : $swatchColour.bg;
}

function getCheckmarkColor({ $swatchColour }: SwatchColorProps) {
  return $swatchColour.invertColour ? "#000" : "#fff";
}
